/**
 * Custom Quill Blot for embedding icons inline in rich text content.
 *
 * This Blot extends Quill's Embed type to allow icons to be inserted at the cursor position as inline elements that
 * render using SVG sprites. Icons are stored in the Delta format with their name and rendered as <span> elements
 * containing SVG references.
 */

// Type reference for Parchment (accessed via Quill.import)
type ParchmentStatic = any;

/** Icon Blot value structure stored in Delta format */
export interface IconBlotValue {
  /** Name of the icon (e.g., 'check-circle', 'home', 'alert') */
  name: string;
}

/**
 * Registers the IconBlot with Quill, enabling icon insertion via the 'icon' embed format.
 *
 * Usage after registration:
 *
 * ```typescript
 * quill.insertEmbed(cursorIndex, 'icon', { name: 'check-circle' });
 * ```
 *
 * @param quillStatic - The Quill static instance to register the blot with
 */
export function registerIconBlot(quillStatic: any): void {
  const InlineBlot = quillStatic.import('blots/embed');

  /**
   * Custom Quill Embed Blot for inline icon rendering.
   *
   * Generates a <span> wrapper containing an SVG element that references the icon sprite. The icon name is stored as a
   * data attribute for persistence across edits. Uses EmbedBlot to allow icons to flow inline with text on the same
   * line.
   */
  class IconBlot extends InlineBlot {
    static blotName = 'icon';
    static tagName = 'SPAN';
    static className = 'hx-embedded-icon';

    /** Creates the DOM node for this blot */
    static create(value: IconBlotValue | string): HTMLElement {
      const iconValue = typeof value === 'string' ? { name: value } : value;
      const node = super.create() as HTMLElement;
      node.setAttribute('contenteditable', 'false');
      node.setAttribute('data-icon', iconValue.name);
      node.style.display = 'inline-flex';
      node.style.alignItems = 'center';
      node.style.verticalAlign = 'middle';
      node.style.margin = '0 2px';
      node.style.padding = '0';
      node.style.lineHeight = '0';

      // Create SVG element with sprite reference
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.classList.add('hx-icon-inline');
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('width', '20');
      svg.setAttribute('height', '20');
      svg.style.width = '1.2em';
      svg.style.height = '1.2em';
      svg.style.fill = 'currentColor';
      svg.style.verticalAlign = 'middle';
      svg.style.display = 'block';

      const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
      const spriteUrl = `/assets/icons/complexcare-icons.svg#ccs-${iconValue.name}`;
      use.setAttributeNS('http://www.w3.org/1999/xlink', 'href', spriteUrl);

      svg.appendChild(use);
      node.appendChild(svg);
      return node;
    }

    /** Extracts the icon value from the DOM node for Delta serialization */
    static value(node: HTMLElement): IconBlotValue {
      const iconName = node.getAttribute('data-icon') || '';
      return { name: iconName };
    }

    /** Returns the Delta representation of this blot */
    value(): IconBlotValue {
      return IconBlot.value(this.domNode as HTMLElement);
    }
  }

  // Register the blot with Quill
  quillStatic.register(IconBlot, true);
}
